import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  FileText,
  Mail,
  MessageSquare,
  Phone,
  ShieldCheck,
  User,
  Wallet,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import StatusBadge from "../../components/dashboard/StatusBadge";
import { CollectorCell } from "../../components/dashboard/tableCellContent";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { MOCK_CASES, formatCurrency } from "../../data/mock/dashboardData";
import { linkWithReturn, navigateWithReturn } from "../../utils/navigationReturn";
import { USE_MOCK_AUTH } from "../../config/api";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import usePermissions from "../../hooks/usePermissions";
import useRole from "../../hooks/useRole";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";
import EscalateCaseModal from "../../components/dashboard/EscalateCaseModal";
import InitiatePaymentModal from "../../components/dashboard/InitiatePaymentModal";
import PaymentPlanModal from "../../components/dashboard/PaymentPlanModal";
import PreApprovalModal from "../../components/dashboard/PreApprovalModal";
import {
  createOpsCallLog,
  fetchOpsCase,
  fetchOpsEscalations,
  normalizeOpsCase,
} from "../../services/institutionWorkspaceApi";
import { showError, showSuccess } from "../../utils/toast";

const CHANNELS = [
  {
    id: "call",
    label: "Phone call",
    icon: Phone,
    tone: "border-orange-200 bg-orange-50/80 text-orange-800 ring-orange-600/15",
    activeTone: "border-orange-400 bg-orange-50 ring-2 ring-orange-400/30",
    guide:
      "Log voice calls made or received on a desk phone, mobile line, or personal handset — not through the SimCollect dialler.",
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: MessageSquare,
    tone: "border-emerald-200 bg-emerald-50/80 text-emerald-800 ring-emerald-600/15",
    activeTone: "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-400/30",
    guide:
      "Log WhatsApp chats initiated or replied to on an agent phone or business account outside the in-app messaging module.",
  },
  {
    id: "email",
    label: "Email",
    icon: Mail,
    tone: "border-violet-200 bg-violet-50/80 text-violet-800 ring-violet-600/15",
    activeTone: "border-violet-400 bg-violet-50 ring-2 ring-violet-400/30",
    guide:
      "Log emails sent or received via Outlook, Gmail, or institution mail — not automated SimCollect email sends.",
  },
  {
    id: "sms",
    label: "SMS",
    icon: MessageSquare,
    tone: "border-sky-200 bg-sky-50/80 text-sky-800 ring-sky-600/15",
    activeTone: "border-sky-400 bg-sky-50 ring-2 ring-sky-400/30",
    guide:
      "Log text messages sent or received from an agent handset or external SMS tool — not bulk SMS dispatched from SimCollect.",
  },
];

const CHANNEL_FROM_CASE = {
  "Phone call": "call",
  SMS: "sms",
  WhatsApp: "whatsapp",
  Email: "email",
};

const CONTACT_RESULTS = [
  "Reached customer",
  "No answer",
  "Voicemail",
  "Wrong number",
  "Line busy",
  "Customer replied",
  "No reply",
  "Delivered",
  "Failed to reach",
];

const OUTCOMES = [
  "Promise to pay recorded",
  "Payment plan agreed",
  "Partial payment taken",
  "Customer educated",
  "No answer",
];

const OTHER_VALUE = "__other__";

const fieldClass =
  "w-full rounded-xl border border-primary/10 bg-white px-4 py-3 text-sm text-primary shadow-none outline-none transition-all duration-200 focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70";

function FormSection({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] sm:p-6">
      <div className="mb-5 flex items-start gap-3 border-b border-primary/[0.06] pb-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/[0.08] text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-sm font-bold text-primary">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-primary/45">{description}</p>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function SelectField({ label, error, children, ...props }) {
  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-primary/70">{label}</label>}
      <select className={fieldClass} {...props}>
        {children}
      </select>
      {error && <p className="text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}

function TextAreaField({ label, error, hint, ...props }) {
  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-medium text-primary/70">{label}</label>}
      <textarea
        className={`${fieldClass} min-h-[96px] resize-y`}
        {...props}
      />
      {hint && !error && <p className="text-xs text-primary/40">{hint}</p>}
      {error && <p className="text-xs font-medium text-red-500">{error}</p>}
    </div>
  );
}

function ChannelPicker({ value, onChange }) {
  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-primary/70">Communication channel</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {CHANNELS.map((item) => {
          const Icon = item.icon;
          const selected = value === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onChange(item.id)}
              className={`flex flex-col items-start gap-2 rounded-xl border px-3.5 py-3 text-left transition-all ${
                selected ? item.activeTone : `${item.tone} opacity-80 hover:opacity-100`
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="text-sm font-bold">{item.label}</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-60">
                Outside app
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SessionFields({ session, onChange, errors }) {
  const set = (key, value) => onChange({ ...session, [key]: value });

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Input
        label="Logged by"
        value={session.loggedBy}
        readOnly
        disabled
        icon={User}
      />
      <Input
        label="Phone number used"
        type="tel"
        placeholder="e.g. 024 123 4567"
        value={session.phone}
        onChange={(event) => set("phone", event.target.value)}
        error={errors.phone}
        required
      />
      <SelectField
        label="Contact result"
        value={session.contactResultChoice}
        onChange={(event) => set("contactResultChoice", event.target.value)}
        error={errors.contactResult}
      >
        <option value="">Select contact result</option>
        {CONTACT_RESULTS.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
        <option value={OTHER_VALUE}>Other</option>
      </SelectField>
      {session.contactResultChoice === OTHER_VALUE ? (
        <Input
          label="Other contact result"
          placeholder="Type the contact result"
          maxLength={120}
          value={session.contactResultOther}
          onChange={(event) => set("contactResultOther", event.target.value)}
          error={errors.contactResultOther}
        />
      ) : null}
    </div>
  );
}

function createInitialSession(loggedBy) {
  return {
    phone: "",
    contactResultChoice: "",
    contactResultOther: "",
    loggedBy: loggedBy ?? "SimCollect agent",
  };
}

function resolveChannelFromCase(recoveryCase) {
  if (!recoveryCase?.channel) return "call";
  return CHANNEL_FROM_CASE[recoveryCase.channel] ?? "call";
}

function resolvedChoice(choice, other) {
  if (choice === OTHER_VALUE) return (other || "").trim();
  return (choice || "").trim();
}

function channelLabel(channelId) {
  return CHANNELS.find((item) => item.id === channelId)?.label ?? channelId;
}

function nothingLeftToCollect(row) {
  const left = Number(row?.outstandingAmount ?? row?.outstanding);
  return Number.isFinite(left) && left <= 0;
}

export default function LogContactPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, token } = useSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  const { isCallCenterAgent } = useRole();
  const canCollect = hasPermission(P.PAYMENTS_INITIATE);
  const canEscalate = isCallCenterAgent && hasPermission(P.CASES_ESCALATE);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [mandateOpen, setMandateOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [caseEscalations, setCaseEscalations] = useState([]);
  const [escalationsLoading, setEscalationsLoading] = useState(false);

  const caseId = searchParams.get("caseId");
  const customerParam = searchParams.get("customer");

  const [recoveryCase, setRecoveryCase] = useState(() => {
    if (!useLiveApi) {
      if (caseId) return MOCK_CASES.find((item) => item.id === caseId);
      if (customerParam) {
        return MOCK_CASES.find((item) => item.customer === customerParam && item.status !== "closed");
      }
    }
    return undefined;
  });
  const [caseLoading, setCaseLoading] = useState(useLiveApi && Boolean(caseId));
  const [moneyRefresh, setMoneyRefresh] = useState(0);
  const liveMoneyTick = useLiveEventTick(/^(payment\.|case\.|preapproval\.|payment_plan\.)/);

  useEffect(() => {
    if (!useLiveApi) {
      if (caseId) setRecoveryCase(MOCK_CASES.find((item) => item.id === caseId));
      else if (customerParam) {
        setRecoveryCase(
          MOCK_CASES.find((item) => item.customer === customerParam && item.status !== "closed")
        );
      }
      return undefined;
    }

    if (!caseId) {
      setRecoveryCase(undefined);
      setCaseLoading(false);
      return undefined;
    }

    let cancelled = false;
    const quiet = Boolean(liveMoneyTick) || moneyRefresh > 0;
    if (!quiet) setCaseLoading(true);
    fetchOpsCase(token, caseId)
      .then((raw) => {
        if (cancelled) return;
        setRecoveryCase(raw ? normalizeOpsCase(raw) : null);
      })
      .catch((error) => {
        if (!cancelled) {
          showError(error.message || "Could not load case.");
          setRecoveryCase(null);
        }
      })
      .finally(() => {
        if (!cancelled) setCaseLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, customerParam, token, useLiveApi, liveMoneyTick, moneyRefresh]);

  useEffect(() => {
    if (!useLiveApi || !caseId) {
      setCaseEscalations([]);
      return undefined;
    }

    let cancelled = false;
    setEscalationsLoading(true);
    fetchOpsEscalations(token, { case_id: caseId }, { force: true })
      .then((data) => {
        if (cancelled) return;
        setCaseEscalations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCaseEscalations([]);
      })
      .finally(() => {
        if (!cancelled) setEscalationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, token, useLiveApi, moneyRefresh]);

  const handleEscalateSuccess = () => {
    setRecoveryCase((prev) => (prev ? { ...prev, escalated: true } : prev));
    setMoneyRefresh((version) => version + 1);
  };

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const defaultChannel = useMemo(
    () => resolveChannelFromCase(recoveryCase),
    [recoveryCase]
  );

  const [channel, setChannel] = useState(defaultChannel);
  const [session, setSession] = useState(() => createInitialSession(user?.name));
  const [outcomeChoice, setOutcomeChoice] = useState("");
  const [outcomeOther, setOutcomeOther] = useState("");
  const [summary, setSummary] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  const contactResult = resolvedChoice(session.contactResultChoice, session.contactResultOther);
  const outcome = resolvedChoice(outcomeChoice, outcomeOther);

  useEffect(() => {
    if (!recoveryCase) return;
    setChannel(resolveChannelFromCase(recoveryCase));
    setSession(createInitialSession(user?.name));
    setOutcomeChoice("");
    setOutcomeOther("");
    setSummary("");
    setFollowUpDate("");
    setFollowUpNotes("");
    setSubmitted(false);
    setErrors({});
  }, [recoveryCase?.id, user?.name]);

  const handleChannelChange = (nextChannel) => {
    setChannel(nextChannel);
  };

  const validate = () => {
    const nextErrors = {};
    if (!session.phone.trim()) nextErrors.phone = "Enter the phone number you used";
    if (!session.contactResultChoice) nextErrors.contactResult = "Select a contact result";
    if (session.contactResultChoice === OTHER_VALUE && !session.contactResultOther.trim()) {
      nextErrors.contactResultOther = "Type the contact result";
    }
    if (!outcomeChoice) nextErrors.outcome = "Select an outcome";
    if (outcomeChoice === OTHER_VALUE && !outcomeOther.trim()) {
      nextErrors.outcomeOther = "Type the outcome";
    }
    if (!summary.trim()) nextErrors.summary = "Add a short summary of what happened";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      if (useLiveApi && recoveryCase?.id) {
        await createOpsCallLog(token, recoveryCase.id, {
          call_type: channelLabel(channel),
          summary,
          contact_result: contactResult,
          channel_details: {
            phone_number: session.phone.trim(),
            contact_result: contactResult,
          },
          logged_at: new Date().toISOString(),
          outcome,
          follow_up: Boolean(followUpDate),
          follow_up_date: followUpDate || null,
          follow_up_notes: followUpNotes.trim() || null,
        });
        showSuccess("Engagement logged.");
      } else {
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
      setSubmitted(true);
    } catch (error) {
      showError(error.message || "Could not log interaction.");
    } finally {
      setLoading(false);
    }
  };

  if (caseLoading) {
    return (
      <PageShell
        title="Log contact"
        description="Record outreach that happened outside SimCollect — calls, SMS, WhatsApp, or email."
        backFallback="/dashboard/cases"
      >
        <div className="rounded-xl border border-primary/10 bg-white px-6 py-16 text-center text-sm text-primary/50">
          Loading case details…
        </div>
      </PageShell>
    );
  }

  if (!recoveryCase) {
    return (
      <PageShell
        title="Log contact"
        description="Record outreach that happened outside SimCollect — calls, SMS, WhatsApp, or email."
        backFallback="/dashboard/cases"
      >
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-primary/15 bg-white px-6 py-16 text-center">
          <AlertCircle className="h-10 w-10 text-amber-500" />
          <h2 className="mt-4 text-lg font-bold text-primary">Case not found</h2>
          <p className="mt-2 max-w-md text-sm text-primary/50">
            Open log contact from a case row in Collection cases so the form is linked to the correct
            recovery record.
          </p>
          <BackNavLink
            fallback="/dashboard/cases"
            fallbackLabel="Go to collection cases"
            className="mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white shadow-md shadow-primary/20 transition-all duration-200 ease-out hover:bg-primary-light hover:shadow-primary/30"
          />
        </div>
      </PageShell>
    );
  }

  if (submitted) {
    return (
      <PageShell
        title="Contact logged"
        description="The outreach session has been saved to the contact centre."
      >
        <div className="mx-auto max-w-lg rounded-xl border border-emerald-200/80 bg-emerald-50/60 px-6 py-10 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
            <CheckCircle2 className="h-7 w-7" />
          </span>
          <h2 className="mt-4 text-lg font-bold text-primary">Outreach logged successfully</h2>
          <p className="mt-2 text-sm text-primary/55">
            <span className="font-semibold text-primary">{channelLabel(channel)}</span> ·{" "}
            <span className="font-semibold text-primary">{outcome}</span> for{" "}
            <span className="font-semibold text-primary">{recoveryCase.customer}</span> (
            {recoveryCase.id}) is now visible in the contact centre.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                navigateWithReturn(
                  navigate,
                  `/dashboard/engagements?customer=${encodeURIComponent(recoveryCase.customer)}&tab=calls`,
                  location
                )
              }
            >
              View contact history
            </Button>
            <BackNavLink fallback="/dashboard/cases" fallbackLabel="Back to cases" />
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Log contact"
      description="Record outreach details after communicating with a debtor outside SimCollect — via call, SMS, WhatsApp, or email."
      backFallback="/dashboard/cases"
    >
      <div className="mb-6 rounded-xl border border-primary/[0.08] bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-primary/40">
              Recovery case
            </p>
            <h2 className="mt-1 text-lg font-bold text-primary">{recoveryCase.customer}</h2>
            <p className="mt-0.5 text-sm text-primary/50">
              {recoveryCase.id} · {recoveryCase.institution}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canCollect && recoveryCase.status !== "closed" && !nothingLeftToCollect(recoveryCase) && (
              <>
                <Button type="button" variant="outline" size="sm" onClick={() => setPaymentOpen(true)}>
                  <Wallet className="h-3.5 w-3.5" />
                  Initiate payment
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setMandateOpen(true)}>
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Send mandate
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setPlanOpen(true)}>
                  <CalendarClock className="h-3.5 w-3.5" />
                  Payment plan
                </Button>
              </>
            )}
            {canEscalate && recoveryCase.status !== "closed" && !recoveryCase.escalated && (
              <Button type="button" variant="outline" size="sm" onClick={() => setEscalateOpen(true)}>
                <AlertTriangle className="h-3.5 w-3.5" />
                Escalate to manager
              </Button>
            )}
            {(recoveryCase.escalated || caseEscalations.length > 0) && (
              <Link
                to={linkWithReturn(
                  `/dashboard/escalations?caseId=${encodeURIComponent(recoveryCase.id)}`,
                  location
                )}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/5"
              >
                View escalations
              </Link>
            )}
            <StatusBadge status={recoveryCase.status} />
            {recoveryCase.escalated && recoveryCase.status !== "closed" && (
              <StatusBadge status="pending" label="Escalated" />
            )}
          </div>
        </div>

        <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <CaseMetric label="Amount owed" value={formatCurrency(recoveryCase.amountOwed)} />
          <CaseMetric label="Amount paid" value={formatCurrency(recoveryCase.amountPaid)} />
          <CaseMetric
            label="Left to pay"
            value={formatCurrency(recoveryCase.outstandingAmount)}
            tone="warn"
          />
          <CaseMetric
            label="Days overdue"
            value={`${recoveryCase.daysOverdue} days`}
            tone={recoveryCase.daysOverdue > 60 ? "danger" : recoveryCase.daysOverdue > 30 ? "warn" : "ok"}
          />
          <div className="rounded-lg bg-white/70 px-3.5 py-2.5 ring-1 ring-primary/[0.06]">
            <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">
              Collector
            </dt>
            <dd className="mt-1">
              <CollectorCell assignedAgent={recoveryCase.assignedAgent} />
            </dd>
          </div>
        </dl>
      </div>

      {(recoveryCase.escalated || caseEscalations.length > 0 || escalationsLoading) && (
        <div className="mb-6 rounded-xl border border-amber-200/80 bg-amber-50/40 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-bold text-primary">Escalation history</h3>
            <Link
              to={linkWithReturn(
                `/dashboard/escalations?caseId=${encodeURIComponent(recoveryCase.id)}`,
                location
              )}
              className="text-xs font-semibold text-primary/70 hover:text-primary"
            >
              Open escalations workspace
            </Link>
          </div>
          {escalationsLoading ? (
            <p className="mt-3 text-sm text-primary/50">Loading escalations…</p>
          ) : caseEscalations.length === 0 ? (
            <p className="mt-3 text-sm text-primary/50">
              This case is on an active escalation. Details appear here once the escalation feed loads.
            </p>
          ) : (
            <ul className="mt-3 space-y-2">
              {caseEscalations.map((item) => (
                <li
                  key={item.id ?? item.escalation_id}
                  className="rounded-lg bg-white/80 px-3.5 py-2.5 text-sm ring-1 ring-primary/[0.06]"
                >
                  <p className="font-semibold text-primary">
                    {item.escalation_id || item.id} · {item.status_label || item.status}
                  </p>
                  <p className="mt-0.5 text-primary/55">{item.reason}</p>
                  <p className="mt-1 text-xs text-primary/45">
                    With {item.assigned_to_name || "—"} · Submitted by {item.submitted_by_name || "—"}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <FormSection
          icon={MessageSquare}
          title="Communication channel"
          description="Choose the channel, then record the number you used and how the contact went. The date and time are saved when you submit."
        >
          <ChannelPicker value={channel} onChange={handleChannelChange} />
          <SessionFields session={session} onChange={setSession} errors={errors} />
        </FormSection>

        <FormSection
          icon={FileText}
          title="Contact outcome"
          description="What happened, and when to come back to this case."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Outcome"
              value={outcomeChoice}
              onChange={(event) => setOutcomeChoice(event.target.value)}
              error={errors.outcome}
            >
              <option value="">Select outcome</option>
              {OUTCOMES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
              <option value={OTHER_VALUE}>Other</option>
            </SelectField>
            {outcomeChoice === OTHER_VALUE ? (
              <Input
                label="Other outcome"
                placeholder="Type the outcome"
                maxLength={255}
                value={outcomeOther}
                onChange={(event) => setOutcomeOther(event.target.value)}
                error={errors.outcomeOther}
              />
            ) : null}
          </div>

          <TextAreaField
            label="Outcome summary"
            placeholder="e.g. Customer agreed to pay ₵ 2,500 via Mobile Money by Friday."
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            error={errors.summary}
            hint="One or two sentences. This is what shows on the engagement history."
          />

          <Input
            label="Follow-up date"
            type="date"
            value={followUpDate}
            onChange={(event) => setFollowUpDate(event.target.value)}
            hint="Set this when the case should come back to you. It appears on the dashboard the morning it is due."
          />

          <TextAreaField
            label="Follow-up notes"
            placeholder="What to do on the follow-up date: call back, confirm payment, ask for a document."
            value={followUpNotes}
            onChange={(event) => setFollowUpNotes(event.target.value)}
          />

        </FormSection>

        <div className="flex flex-wrap items-center justify-end gap-3 rounded-xl border border-primary/[0.07] bg-white px-5 py-4">
          <BackNavLink
            fallback="/dashboard/cases"
            fallbackLabel="Cancel"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-primary/20 bg-transparent px-4 py-2 text-sm font-bold text-primary transition-all duration-200 ease-out hover:border-primary/35 hover:bg-primary/5"
          />
          <Button variant="primary" size="sm" type="submit" loading={loading}>
            Save contact log
          </Button>
        </div>
      </form>

      <InitiatePaymentModal
        caseRow={paymentOpen ? recoveryCase : null}
        onClose={() => setPaymentOpen(false)}
        onComplete={() => setMoneyRefresh((version) => version + 1)}
      />
      <PreApprovalModal caseRow={mandateOpen ? recoveryCase : null} onClose={() => setMandateOpen(false)} />
      <PaymentPlanModal caseRow={planOpen ? recoveryCase : null} onClose={() => setPlanOpen(false)} />
      <EscalateCaseModal
        caseRow={escalateOpen ? recoveryCase : null}
        onClose={() => setEscalateOpen(false)}
        onSuccess={handleEscalateSuccess}
      />
    </PageShell>
  );
}

function CaseMetric({ label, value, tone }) {
  const toneClass =
    tone === "danger"
      ? "text-red-600"
      : tone === "warn"
        ? "text-amber-600"
        : tone === "ok"
          ? "text-emerald-600"
          : "text-primary";

  return (
    <div className="rounded-lg bg-white/70 px-3.5 py-2.5 ring-1 ring-primary/[0.06]">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">{label}</dt>
      <dd className={`mt-1 text-sm font-bold tabular-nums ${toneClass}`}>{value}</dd>
    </div>
  );
}
